import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { FifBranch, FifBranchService } from '@lmc-app/core/services/fif-branch.service';
import { BranchDialogComponent, BranchDialogData } from './branch-dialog/branch-dialog.component';

@Component({
  selector: 'app-fif-branch',
  templateUrl: './fif-branch.component.html',
  styleUrls: ['./fif-branch.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTableModule,
    MatTooltipModule,
  ],
})
export class FifBranchComponent implements OnInit, OnDestroy {
  readonly displayedColumns = ['dprn', 'name', 'address', 'actions'];
  readonly pageSizeOptions = [50, 100];

  branches: FifBranch[] = [];
  rowCount = 0;
  pageIndex = 0;
  pageSize = 50;
  isLoading = false;

  searchControl = new FormControl<string>('', { nonNullable: true });

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fifBranchService: FifBranchService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadBranches();

    this.searchControl.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadBranches();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadBranches(): void {
    this.isLoading = true;
    this.fifBranchService
      .list(this.pageIndex + 1, this.pageSize, this.searchControl.value)
      .subscribe({
        next: (res) => {
          this.branches = res.results;
          this.rowCount = res.rowCount;
          this.isLoading = false;
        },
        error: (err) => {
          const msg = err.error?.message || 'Failed to load branches';
          this.snackBar.open(msg, 'Close', { duration: 5000 });
          this.isLoading = false;
        },
      });
  }

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadBranches();
  }

  openCreate(): void {
    this.openDialog({});
  }

  openEdit(branch: FifBranch): void {
    this.openDialog({ branch });
  }

  onDelete(branch: FifBranch): void {
    if (!confirm(`Delete branch ${branch.dprn} (${branch.name})?`)) {
      return;
    }
    this.fifBranchService.delete(branch.id).subscribe({
      next: () => {
        this.snackBar.open('Branch deleted', 'Close', { duration: 4000 });
        // If we just removed the last row on the current page, step back a page.
        if (this.branches.length === 1 && this.pageIndex > 0) {
          this.pageIndex -= 1;
        }
        this.loadBranches();
      },
      error: (err) => {
        const msg = err.error?.message || 'Failed to delete branch';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
      },
    });
  }

  private openDialog(data: BranchDialogData): void {
    const ref = this.dialog.open(BranchDialogComponent, {
      width: '520px',
      data,
      autoFocus: false,
    });

    ref.afterClosed().subscribe((saved?: FifBranch) => {
      if (saved) {
        this.loadBranches();
      }
    });
  }
}